import React, { useState, useEffect } from 'react';
import { IoStorefrontOutline, IoRefreshOutline } from 'react-icons/io5';
import { searchPartnerPlaces } from '../../api/PartnerPlaceApi';
import PartnerPlaceItemContainer from './PartnerPlaceItemContainer';

const PartnerPlaceContainer = ({ onPlaceSelect }) => {
    const [partnerPlaces, setPartnerPlaces] = useState([]);
    const [lastDistance, setLastDistance] = useState(null);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    // 초기 데이터 로드
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await searchPartnerPlaces();
            setPartnerPlaces(response.partnerPlaceSearchDtoList || []);
            setLastDistance(response.lastDistance);
            setHasNext(response.hasNext);
        } catch (error) {
            console.error('제휴매장 검색 실패:', error);
            setError('제휴매장을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const loadMoreData = async () => {
        if (!hasNext || loadingMore) return;

        try {
            setLoadingMore(true);
            const response = await searchPartnerPlaces(lastDistance);
            setPartnerPlaces(prev => [...prev, ...(response.partnerPlaceSearchDtoList || [])]);
            setLastDistance(response.lastDistance);
            setHasNext(response.hasNext);
        } catch (error) {
            console.error('추가 제휴매장 검색 실패:', error);
            setError('추가 제휴매장을 불러오는데 실패했습니다.');
        } finally {
            setLoadingMore(false);
        }
    };

    const handleRetry = () => {
        loadInitialData();
    };

    if (loading) {
        return (
            <div className="p-4">
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                    <p className="text-gray-500 mt-2">제휴매장을 검색하고 있습니다...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <div className="text-center py-8">
                    <IoStorefrontOutline size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={handleRetry}
                        className="flex items-center justify-center mx-auto px-4 py-2 bg-amber-500 text-white rounded-lg active:bg-amber-600 transition-colors"
                    >
                        <IoRefreshOutline size={16} className="mr-2" />
                        다시 시도
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3">
            {partnerPlaces.length === 0 ? (
                <div className="text-center py-8">
                    <IoStorefrontOutline size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">주변에 제휴매장이 없습니다</p>
                    <p className="text-sm text-gray-500">다른 지역에서 검색해보세요</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {partnerPlaces.map((partnerPlace, index) => (
                        <PartnerPlaceItemContainer
                            key={`${partnerPlace.id}-${index}`}
                            partnerPlace={partnerPlace}
                            onSelect={onPlaceSelect}
                        />
                    ))}

                    {/* 더 가져오기 버튼 */}
                    {hasNext && (
                        <div className="text-center pt-4">
                            <button
                                onClick={loadMoreData}
                                disabled={loadingMore}
                                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loadingMore ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                                        로딩 중...
                                    </div>
                                ) : (
                                    '더 가져오기'
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PartnerPlaceContainer;
